// Analytics charts (ADR-0045): plain SVG, no chart dependency. One series per chart (small multiples
// instead of two scales), the brand hue for magnitude, recessive axes, text in text tokens, a hover
// tooltip on every mark and a table view for each chart next to it.
import { useId, useMemo, useState } from "react";
import { cn } from "@/ui/cn";
import { formatNumber } from "./format";

export interface DailyPoint {
  readonly day: string;
  readonly value: number;
}

const shortDay = new Intl.DateTimeFormat("es", { day: "numeric", month: "short", timeZone: "UTC" });
const longDay = new Intl.DateTimeFormat("es", { weekday: "short", day: "numeric", month: "long", timeZone: "UTC" });
const asDate = (day: string) => new Date(`${day}T00:00:00Z`);

/** Every day of [from, to] (missing days are zero, never interpolated). */
export function fillDays(
  from: string,
  to: string,
  points: ReadonlyArray<{ day: string; value: number }>
): DailyPoint[] {
  const by = new Map(points.map((p) => [p.day, p.value]));
  const out: DailyPoint[] = [];
  for (let t = asDate(from).getTime(); t <= asDate(to).getTime(); t += 86_400_000) {
    const day = new Date(t).toISOString().slice(0, 10);
    out.push({ day, value: by.get(day) ?? 0 });
  }
  return out;
}

function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

/** Daily line + area of ONE measure, with crosshair and tooltip. */
export function DailyChart({ title, total, points }: { title: string; total: number; points: readonly DailyPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const id = useId();
  const W = 320;
  const H = 120;
  const pad = { l: 34, r: 8, t: 8, b: 20 };
  const max = niceMax(Math.max(0, ...points.map((p) => p.value)));
  const n = points.length;
  const x = (i: number) => pad.l + (n <= 1 ? (W - pad.l - pad.r) / 2 : (i * (W - pad.l - pad.r)) / (n - 1));
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join("");
  const area = n > 0 ? `${line}L${x(n - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z` : "";
  const ticks = [0, max / 2, max];
  const labelEvery = Math.max(1, Math.ceil(n / 4));
  const current = hover !== null ? points[hover] : undefined;
  return (
    <figure className="grid gap-2 rounded-md border border-line bg-surface p-3" aria-labelledby={`${id}-t`}>
      <figcaption className="flex items-baseline justify-between gap-2">
        <span id={`${id}-t`} className="text-xs font-medium text-muted">
          {title}
        </span>
        <span className="text-sm font-semibold tabular-nums">{formatNumber(total)}</span>
      </figcaption>
      <div className="relative">
        <svg
          viewBox={`0 0 ${String(W)} ${String(H)}`}
          className="block h-auto w-full"
          role="img"
          aria-label={`${title} por día`}
          onMouseLeave={() => {
            setHover(null);
          }}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const px = ((e.clientX - r.left) / r.width) * W;
            if (n === 0) return;
            const i = Math.round(((px - pad.l) / (W - pad.l - pad.r)) * (n - 1));
            setHover(Math.min(n - 1, Math.max(0, i)));
          }}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} className="stroke-line" strokeWidth={1} />
              <text x={pad.l - 6} y={y(t) + 3} textAnchor="end" className="fill-subtle text-[9px] tabular-nums">
                {formatNumber(t, true)}
              </text>
            </g>
          ))}
          {points.map((p, i) =>
            i % labelEvery === 0 || i === n - 1 ? (
              <text
                key={p.day}
                x={x(i)}
                y={H - 4}
                textAnchor={n > 1 && i === n - 1 ? "end" : i === 0 && n > 1 ? "start" : "middle"}
                className="fill-subtle text-[9px]"
              >
                {shortDay.format(asDate(p.day))}
              </text>
            ) : null
          )}
          <path d={area} className="fill-brand/10" />
          <path d={line} fill="none" className="stroke-brand" strokeWidth={2} strokeLinejoin="round" />
          {current && hover !== null ? (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={y(0)} className="stroke-line-strong" strokeWidth={1} />
              <circle cx={x(hover)} cy={y(current.value)} r={4} className="fill-brand stroke-surface" strokeWidth={2} />
            </g>
          ) : null}
        </svg>
        {current && hover !== null ? (
          <div
            role="status"
            className="pointer-events-none absolute top-0 rounded-md border border-line bg-raised px-2 py-1 text-xs shadow-overlay"
            style={{ left: `${String(Math.min(70, Math.max(0, (x(hover) / W) * 100 - 15)))}%` }}
          >
            <div className="text-muted">{longDay.format(asDate(current.day))}</div>
            <div className="font-semibold tabular-nums">{formatNumber(current.value)}</div>
          </div>
        ) : null}
      </div>
    </figure>
  );
}

export interface BarDatum {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** Shown next to the bar instead of the raw value (e.g. "12 · 34 %"). */
  readonly display?: string;
  readonly hint?: string;
}

/** Horizontal bars of ONE measure (magnitude): sorted by the caller, value labels in text tokens. */
export function BarList({
  data,
  label,
  empty = "Sin datos en el período.",
  max: fixedMax,
  className,
}: {
  data: readonly BarDatum[];
  label: string;
  empty?: string;
  max?: number;
  className?: string;
}) {
  const max = useMemo(() => fixedMax ?? Math.max(0, ...data.map((d) => d.value)), [data, fixedMax]);
  if (data.length === 0) return <p className="text-sm text-muted">{empty}</p>;
  return (
    // min-w-0 down to the row: a long name truncates inside its column instead of covering the next one.
    <ul className={cn("grid min-w-0 gap-2", className)} aria-label={label}>
      {data.map((d) => (
        <li key={d.key} className="group grid min-w-0 gap-1" title={d.hint ?? `${d.label}: ${formatNumber(d.value)}`}>
          <div className="flex min-w-0 items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">{d.label}</span>
            <span className="shrink-0 tabular-nums text-muted">{d.display ?? formatNumber(d.value)}</span>
          </div>
          <div className="h-2 rounded-sm bg-sunken" aria-hidden="true">
            <div
              className="h-2 rounded-sm bg-brand transition-[width] duration-300 group-hover:bg-brand-strong"
              style={{ width: `${String(max > 0 ? Math.max(d.value > 0 ? 1.5 : 0, (d.value / max) * 100) : 0)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
